/**
 * Service for persisting and retrieving booking filters from localStorage
 */

export interface BookingFilters {
  searchQuery?: string;
  statusFilter?: string[];
  officersFilter?: string[];
  serviceFilter?: string;
  dateRangeFilter?: {
    startDate: Date | null;
    endDate: Date | null;
  };
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
}

const STORAGE_KEY = 'bookings_filters_v1';

export class FilterPersistenceService {
  /**
   * Save filters to localStorage
   */
  static saveFilters(filters: BookingFilters): void {
    try {
      // Convert Date objects to ISO strings for storage
      const filtersToStore = {
        ...filters,
        dateRangeFilter: filters.dateRangeFilter ? {
          startDate: filters.dateRangeFilter.startDate ? filters.dateRangeFilter.startDate.toISOString() : null,
          endDate: filters.dateRangeFilter.endDate ? filters.dateRangeFilter.endDate.toISOString() : null,
        } : undefined,
      };
      const serializedFilters = JSON.stringify(filtersToStore);
      localStorage.setItem(STORAGE_KEY, serializedFilters);
    } catch (error) {
      console.error('Error saving filters to localStorage:', error);
    }
  }

  /**
   * Load filters from localStorage
   */
  static loadFilters(): BookingFilters | null {
    try {
      const serializedFilters = localStorage.getItem(STORAGE_KEY);
      if (serializedFilters) {
        const filters = JSON.parse(serializedFilters);

        // Convert date strings back to Date objects if they exist
        if (filters.dateRangeFilter) {
          if (filters.dateRangeFilter.startDate) {
            filters.dateRangeFilter.startDate = new Date(filters.dateRangeFilter.startDate);
          }
          if (filters.dateRangeFilter.endDate) {
            filters.dateRangeFilter.endDate = new Date(filters.dateRangeFilter.endDate);
          }
        }

        return filters;
      }
      return null;
    } catch (error) {
      console.error('Error loading filters from localStorage:', error);
      return null;
    }
  }

  /**
   * Clear all saved filters
   */
  static clearFilters(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing filters from localStorage:', error);
    }
  }

  /**
   * Check if filters are saved
   */
  static hasPersistedFilters(): boolean {
    try {
      return localStorage.getItem(STORAGE_KEY) !== null;
    } catch (error) {
      console.error('Error checking for persisted filters:', error);
      return false;
    }
  }
}
